/**
 * downloadEventMediaZip
 *
 * Calls the backend "bundle all event media into a ZIP" endpoints and triggers a browser
 * download. Mirrors the `downloadCoa.ts` pattern (EP-13) since these endpoints stream raw binary
 * content directly rather than a presigned S3 URL, so RTK Query's generated hooks (which assume a
 * JSON body) aren't used here.
 *
 * The backend exposes two distinct endpoints with different auth (EP-14):
 *   - Organiser:   GET /api/v1/events/{eventId}/media/zip
 *                  Authenticated with the organiser's Keycloak JWT (see {@link getApiToken}).
 *   - Participant: GET /api/v1/event/participant/media/zip
 *                  Authenticated with the participant's magic-link token (`?tk=`), sent as a
 *                  Bearer token — the participant has no Keycloak session.
 */

import { getApiToken } from '@proaxiv/server-api'

const DEFAULT_ZIP_FILENAME = 'event-media.zip'

/**
 * Triggers a browser download of every media file uploaded for an event, bundled into a single
 * ZIP, as the event organiser. Used from the Overview tab's Event Media panel.
 *
 * @throws Error with a human-readable message on non-2xx responses.
 */
export async function downloadEventMediaZip(eventId: string): Promise<void> {
  const token = getApiToken()
  const headers: HeadersInit = token ? { Authorization: `Bearer ${token}` } : {}
  const res = await fetch(`/api/v1/events/${eventId}/media/zip`, { headers })
  if (!res.ok) {
    throw new Error(`Media download failed (HTTP ${res.status})`)
  }
  const blob = await res.blob()
  triggerDownload(blob, DEFAULT_ZIP_FILENAME)
}

/**
 * Triggers a browser download of every event media file currently visible to the caller, bundled
 * into a single ZIP, as the participant themselves. Used from the participant's ticket page.
 *
 * @throws Error with a human-readable message on non-2xx responses.
 */
export async function downloadOwnEventMediaZip(participantToken: string): Promise<void> {
  const res = await fetch('/api/v1/event/participant/media/zip', {
    headers: { Authorization: `Bearer ${participantToken}` },
  })
  if (!res.ok) {
    throw new Error(`Media download failed (HTTP ${res.status})`)
  }
  const blob = await res.blob()
  triggerDownload(blob, DEFAULT_ZIP_FILENAME)
}

function triggerDownload(blob: Blob, filename: string): void {
  const url = URL.createObjectURL(blob)
  const a = document.createElement('a')
  a.href = url
  a.download = filename
  document.body.appendChild(a)
  a.click()
  document.body.removeChild(a)
  URL.revokeObjectURL(url)
}
